import { push, replace } from 'connected-next-router';
import isEqual from 'lodash/isEqual';
import sortBy from 'lodash/sortBy';
import { getQueryStringParams, addQueryStringParam } from '@/Framework/url/helpers/queryString';
import { setPageFilters } from '@/finsight/application/actions/pageFilters/actions';
import Product from '@/finsight/domain/Product';
import Region from '@/finsight/domain/Region';
import { IProduct } from '@/finsight/domain/vo/Product';
import { IRegion } from '@/finsight/domain/vo/Region';

export const initProductRegionFilters = (
  productsCollection: IProduct[],
  regionsCollection: IRegion[],
  shouldSetFilterFromHeader: boolean = false,
) => (dispatch, getState) => {
  const state = getState();
  // Parse ?products...&regions=... URL
  const {
    products: urlProducts,
    regions: urlRegions,
  } = getQueryStringParams(state.router.location);
  const urlProductsArr = urlProducts ? urlProducts.split(',') : ['ABS'];
  const urlRegionsArray = urlRegions ? urlRegions.split(',') : ['USOA'];

  const allProductsAbbreviations = sortBy(productsCollection.map((product) => product.abbreviation));
  const allRegionsAbbreviations = sortBy(regionsCollection.map((region) => region.abbreviation));

  const urlProductsAbbreviations = allProductsAbbreviations.filter((product) => urlProductsArr.includes(product));
  const urlRegionsAbbreviations = allRegionsAbbreviations.filter((region) => urlRegionsArray.includes(region));

  let newFilters: any = {};

  const getDefaultProductsForUrl = (): string => (
    shouldSetFilterFromHeader
      ? urlProductsAbbreviations.join(',')
      : allProductsAbbreviations.join(',')
  );

  const getDefaultRegionsForUrl = (): string => (
    shouldSetFilterFromHeader
      ? urlRegionsAbbreviations.join(',')
      : allRegionsAbbreviations.join(',')
  );

  // validate ?products=... in url
  const {
    updatedQuery: validProducts,
    hasChanged: productsChanged,
  } = validateUrlFilter(urlProducts, productsCollection, getDefaultProductsForUrl());

  // validate ?regions=... in url
  const {
    updatedQuery: validRegions,
    hasChanged: regionsChanged,
  } = validateUrlFilter(urlRegions, regionsCollection, getDefaultRegionsForUrl());

  // if we should add something as the ?queryString
  if (productsChanged || regionsChanged) {
    newFilters.queryParam = addQueryStringParam(state.router.location, {
      products: validProducts,
      regions: validRegions,
    });
  }

  const productIds = Product.getIDsByAbbreviations(productsCollection, validProducts);
  const regionIds = Region.getIDsByAbbreviations(regionsCollection, validRegions);
  const {
    productIds: pageFiltersProductIds,
    regionIds: pageFiltersRegionIds,
  } = state.pageFilters;

  // compare what we have in URL (or want to have there) with current pageFilters
  if (!isEqual(sortBy(pageFiltersProductIds), sortBy(productIds))) {
    newFilters.productIds = productIds;
  }
  if (!isEqual(sortBy(pageFiltersRegionIds), sortBy(regionIds))) {
    newFilters.regionIds = regionIds;
  }

  // If we have URL query/store updates - apply and save them
  if (Object.keys(newFilters).length) {
    dispatch(setProductRegionFilters(newFilters, 'replace'));
    return true;
  }

  return false;
};

export const setProductRegionFilters = (
  filters,
  historyMethod: string = 'push',
  isPreventSSR : boolean = false,
) => (dispatch, getState) => {
  const state = getState();
  const { location } = state.router;
  const historyMethods = { push, replace };
  const withRedirect = (!!historyMethod !== false);

  let productIds = filters.productIds || state.pageFilters.productIds;
  let regionIds = filters.regionIds || state.pageFilters.regionIds;

  // set new OR update product/region
  dispatch(setPageFilters({
    productIds,
    regionIds,
  }));

  // redirect to the new set of filters
  if (filters.queryParam && withRedirect) {
    // invoke push or replace
    dispatch(historyMethods[historyMethod](
      `${ location.pathname }?${ filters.queryParam }`,
      undefined,
      { shallow: isPreventSSR },
    ));
  }
};

export const validateUrlFilter = (query: string = '', collection, defaultValue: string) => {
  let result = {
    updatedQuery: '',
    hasChanged: false,
  };

  // default if not present in the URL
  if (!query) {
    result.updatedQuery = defaultValue;
    result.hasChanged = true;
    return result;
  }

  const abbreviations = sortBy(collection.map((item) => item.abbreviation));

  // make string an array to filter it
  const queryArray = query.split(',');

  const baseLength = queryArray.length;

  // leave only real items, which exist in collection
  const queryArrayFiltered = queryArray.filter((item) => abbreviations.includes(item));

  // filtered array is different - redirect to filtered abbreviations
  if (queryArrayFiltered.length > 0 && queryArrayFiltered.length !== baseLength) {
    result.updatedQuery = queryArrayFiltered.join(',');
    result.hasChanged = true;
  } else if (queryArrayFiltered.length === 0) { // filtered array has no items left - all removed, set default
    result.updatedQuery = defaultValue;
    result.hasChanged = true;
  } else {
    result.updatedQuery = queryArrayFiltered.join(','); // query is unchanged - convert back to string
  }

  return result;
};
